// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

import React, { useState, useMemo, useEffect } from "react";
import { sGetRaw, sSetRaw } from "../lib/scopedStorage";
import type { SupabaseStorageProvider } from "../lib/supabaseStorage";
import type { UserProfile, BestPractice, BestPracticeCategory } from "../types";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";
const LS_KEY = "oe_best_practices_v1";
const TEAL = "#4fd1c5";

function lsGet<T>(key: string, fallback: T): T {
  try { const s = sGetRaw(key); return s ? JSON.parse(s) : fallback; } catch { return fallback; }
}
function lsSet(key: string, val: unknown) {
  try { sSetRaw(key, JSON.stringify(val)); } catch {}
}

export const ALL_DIVS = [
  {id:"div1", code:"Div 01", name:"General Requirements"},
  {id:"div2", code:"Div 02", name:"Existing Conditions"},
  {id:"div3", code:"Div 03", name:"Concrete"},
  {id:"div4", code:"Div 04", name:"Masonry"},
  {id:"div5", code:"Div 05", name:"Metals"},
  {id:"div6", code:"Div 06", name:"Wood, Plastics & Composites"},
  {id:"div7", code:"Div 07", name:"Thermal & Moisture Protection"},
  {id:"div8", code:"Div 08", name:"Openings"},
  {id:"div9", code:"Div 09", name:"Finishes"},
  {id:"div10",code:"Div 10", name:"Specialties"},
  {id:"div11",code:"Div 11", name:"Equipment"},
  {id:"div22",code:"Div 22", name:"Plumbing"},
  {id:"div23",code:"Div 23", name:"HVAC"},
  {id:"div25",code:"Div 25", name:"Integrated Automation"},
  {id:"div26",code:"Div 26", name:"Electrical"},
  {id:"div27",code:"Div 27", name:"Communications"},
  {id:"div31",code:"Div 31", name:"Earthwork"},
  {id:"div32",code:"Div 32", name:"Exterior Improvements"},
  {id:"div33",code:"Div 33", name:"Utilities"},
];

export const CATEGORIES: { id: BestPracticeCategory; label: string; color: string; bg: string }[] = [
  { id: "eo",         label: "E&O Risk",          color: "#fc8181", bg: "#fc818118" },
  { id: "scope_gap",  label: "Scope Gap",          color: "#f6ad55", bg: "#f6ad5518" },
  { id: "ve",         label: "VE Opportunity",     color: "#4fd1c5", bg: "#4fd1c518" },
  { id: "code",       label: "Code Requirement",   color: "#63b3ed", bg: "#63b3ed18" },
  { id: "owner_pref", label: "Owner Preference",   color: "#b794f4", bg: "#b794f418" },
];

export const DEFAULT_BEST_PRACTICES: BestPractice[] = [
  {id:"bp01",description:"Project superintendent and PM overlap at start/closeout — verify duration and rate in GC scope",divisionTags:["div1"],category:"eo",sourceNote:"Commonly excluded from GC lump-sum scopes",addedAt:"2025-01-01"},
  {id:"bp02",description:"Special inspection program fees: concrete, masonry, welding, high-strength bolts, soils",divisionTags:["div1"],category:"code",sourceNote:"Required by IBC — often billed directly to Owner, not GC",addedAt:"2025-01-01"},
  {id:"bp03",description:"MEP commissioning and TAB (test, adjust, balance) — verify included in mechanical sub scope",divisionTags:["div1","div23"],category:"scope_gap",addedAt:"2025-01-01"},
  {id:"bp04",description:"Hazardous materials survey and abatement — required before any demolition work",divisionTags:["div2"],category:"code",sourceNote:"Required by EPA/OSHA on pre-1980 buildings",addedAt:"2025-01-01"},
  {id:"bp05",description:"Underground utility locating (SUE) and potholing before excavation",divisionTags:["div2","div31"],category:"eo",addedAt:"2025-01-01"},
  {id:"bp06",description:"Winter concrete protection — heated enclosures, curing blankets, heated mix water (below 40°F)",divisionTags:["div3"],category:"scope_gap",sourceNote:"Often scoped as an allowance if pour dates are uncertain",addedAt:"2025-01-01"},
  {id:"bp07",description:"Structural steel shop drawing review fee by EOR — confirm responsibility in GC or steel sub scope",divisionTags:["div5"],category:"eo",addedAt:"2025-01-01"},
  {id:"bp08",description:"Hardware for custom millwork — pulls, hinges, soft-close — owner selection commonly drives cost delta",divisionTags:["div6"],category:"owner_pref",addedAt:"2025-01-01"},
  {id:"bp09",description:"Envelope mock-up fabrication and testing per project specifications",divisionTags:["div7","div8"],category:"eo",sourceNote:"Required on most high-performance and institutional projects",addedAt:"2025-01-01"},
  {id:"bp10",description:"AAMA 502 water infiltration testing of installed windows and curtain wall",divisionTags:["div7","div8"],category:"code",addedAt:"2025-01-01"},
  {id:"bp11",description:"Air barrier continuity inspection and blower door testing",divisionTags:["div7"],category:"code",sourceNote:"Required under energy codes in many jurisdictions",addedAt:"2025-01-01"},
  {id:"bp12",description:"Roofing manufacturer warranty inspection — required for 20-year NDL warranty issuance",divisionTags:["div7"],category:"scope_gap",addedAt:"2025-01-01"},
  {id:"bp13",description:"Window and door hardware sets — owner selection often drives significant cost above allowance",divisionTags:["div8"],category:"owner_pref",addedAt:"2025-01-01"},
  {id:"bp14",description:"Glass color, reflectivity, and pattern mockups for owner/architect approval (budget 2+ rounds)",divisionTags:["div8"],category:"owner_pref",addedAt:"2025-01-01"},
  {id:"bp15",description:"Paint color mockups — typically 2+ approval rounds; budget half-day painter labor per round",divisionTags:["div9"],category:"owner_pref",addedAt:"2025-01-01"},
  {id:"bp16",description:"Large-format tile layout mockup for pattern/joint confirmation before installation",divisionTags:["div9"],category:"owner_pref",addedAt:"2025-01-01"},
  {id:"bp17",description:"HVAC equipment startup by manufacturer representative — required to activate warranty",divisionTags:["div23"],category:"scope_gap",addedAt:"2025-01-01"},
  {id:"bp18",description:"Low-voltage rough-in for AV, security, and access control — coordinate scope gap with AV/security sub",divisionTags:["div26","div27"],category:"scope_gap",addedAt:"2025-01-01"},
  {id:"bp19",description:"Asphalt driveway and roadway restoration after utility work",divisionTags:["div32","div33"],category:"eo",sourceNote:"Frequently becomes a CO when not explicitly stated in sitework scope",addedAt:"2025-01-01"},
  {id:"bp20",description:"Erosion control removal, grading restoration, and site cleanup at substantial completion",divisionTags:["div32"],category:"scope_gap",addedAt:"2025-01-01"},
];

interface Props {
  storage?: SupabaseStorageProvider;
  user?: UserProfile;
  onBack: () => void;
}

export default function LessonsLibrary({ onBack }: Props) {
  const [isDarkMode] = useState(() => {
    try { const s = sGetRaw("oe_dark_mode"); return s === null ? true : s === "true"; } catch { return true; }
  });
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;

  const [items, setItems] = useState<BestPractice[]>(() => {
    const saved = lsGet<BestPractice[]>(LS_KEY, []);
    return saved.length > 0 ? saved : DEFAULT_BEST_PRACTICES;
  });

  useEffect(() => { lsSet(LS_KEY, items); }, [items]);

  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<string>("all");
  const [filterDiv, setFilterDiv] = useState<string>("all");
  const [editItem, setEditItem] = useState<Partial<BestPractice> | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let out = items;
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(b => b.description.toLowerCase().includes(q) || (b.sourceNote || "").toLowerCase().includes(q));
    }
    if (filterCat !== "all") out = out.filter(b => b.category === filterCat);
    if (filterDiv !== "all") out = out.filter(b => b.divisionTags.includes(filterDiv));
    return out;
  }, [items, search, filterCat, filterDiv]);

  // Count per category for filter bar
  const catCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    items.forEach(b => { counts[b.category] = (counts[b.category] || 0) + 1; });
    return counts;
  }, [items]);

  const handleSave = (bp: BestPractice) => {
    setItems(prev => {
      const idx = prev.findIndex(x => x.id === bp.id);
      if (idx >= 0) { const n = [...prev]; n[idx] = bp; return n; }
      return [...prev, bp];
    });
    setEditItem(null);
  };

  const handleDelete = (id: string) => {
    setItems(prev => prev.filter(x => x.id !== id));
    setDeleteConfirmId(null);
  };

  const handleResetDefaults = () => {
    if (window.confirm("Reset to default library? Your custom entries will be replaced.")) {
      setItems(DEFAULT_BEST_PRACTICES);
    }
  };

  const catMeta = (id: string) => CATEGORIES.find(c => c.id === id) || CATEGORIES[0];
  const divLabel = (id: string) => { const d = ALL_DIVS.find(x => x.id === id); return d ? d.code : id; };

  return (
    <div style={{ minHeight: "100vh", background: T.bgApp, fontFamily: FONT, color: T.textPrimary, display: "flex", flexDirection: "column" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 28px", borderBottom: `1px solid ${T.borderPrimary}`, background: T.bgPanel, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 12 }}>
            ← Back
          </button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#f6ad55" }}>⚑</span> Lessons &amp; Best Practices
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
              {items.length} entries · shared across all projects · never printed in client reports
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={handleResetDefaults}
            style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 11 }}>
            Reset Defaults
          </button>
          <button
            onClick={() => setEditItem({ id: `bp${Date.now()}`, description: "", divisionTags: [], category: "eo", addedAt: new Date().toISOString().slice(0, 10) })}
            style={{ background: TEAL, border: "none", color: "#0f1117", padding: "8px 20px", borderRadius: 6, fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            + New Entry
          </button>
        </div>
      </div>

      {/* ── Category pills bar ── */}
      <div style={{ display: "flex", gap: 6, padding: "12px 28px", borderBottom: `1px solid ${T.borderPrimary}`, background: T.bgApp, flexWrap: "wrap" }}>
        <button onClick={() => setFilterCat("all")}
          style={{ padding: "4px 12px", borderRadius: 999, border: `1px solid ${filterCat === "all" ? TEAL : T.borderSecondary}`, background: filterCat === "all" ? TEAL + "18" : "transparent", color: filterCat === "all" ? TEAL : T.textMuted, cursor: "pointer", fontFamily: FONT, fontSize: 11, fontWeight: filterCat === "all" ? 700 : 400 }}>
          All <span style={{ opacity: .6 }}>({items.length})</span>
        </button>
        {CATEGORIES.map(c => (
          <button key={c.id} onClick={() => setFilterCat(filterCat === c.id ? "all" : c.id)}
            style={{ padding: "4px 12px", borderRadius: 999, border: `1px solid ${filterCat === c.id ? c.color : T.borderSecondary}`, background: filterCat === c.id ? c.bg : "transparent", color: filterCat === c.id ? c.color : T.textMuted, cursor: "pointer", fontFamily: FONT, fontSize: 11, fontWeight: filterCat === c.id ? 700 : 400 }}>
            {c.label} <span style={{ opacity: .6 }}>({catCounts[c.id] || 0})</span>
          </button>
        ))}
      </div>

      {/* ── Search + Division filter ── */}
      <div style={{ display: "flex", gap: 10, padding: "12px 28px", borderBottom: `1px solid ${T.borderPrimary}`, background: T.bgApp, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search descriptions and notes…"
          style={{ flex: "1 1 220px", minWidth: 160, background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "7px 12px", outline: "none" }} />
        <select value={filterDiv} onChange={e => setFilterDiv(e.target.value)}
          style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "7px 10px" }}>
          <option value="all">All Divisions</option>
          {ALL_DIVS.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
        </select>
        {(search || filterCat !== "all" || filterDiv !== "all") && (
          <button onClick={() => { setSearch(""); setFilterCat("all"); setFilterDiv("all"); }}
            style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 11 }}>
            Clear
          </button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 11, color: T.textMuted }}>{filtered.length} shown</div>
      </div>

      {/* ── List ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 28px", display: "flex", flexDirection: "column", gap: 8 }}>

        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0", color: T.textMuted }}>
            <div style={{ fontSize: 32, marginBottom: 14, color: "#f6ad55" }}>⚑</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>No entries match</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>Clear your filters or add a new lesson</div>
          </div>
        )}

        {filtered.map(bp => {
          const cat = catMeta(bp.category);
          return (
            <div key={bp.id} style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "14px 16px", display: "flex", gap: 12, alignItems: "flex-start", transition: "border-color .12s" }}>
              {/* Category color bar */}
              <div style={{ width: 3, borderRadius: 3, background: cat.color, flexShrink: 0, alignSelf: "stretch", minHeight: 20 }} />

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Tags row */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 7 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", color: cat.color, background: cat.bg, border: `1px solid ${cat.color}44`, borderRadius: 10, padding: "2px 9px", flexShrink: 0 }}>
                    {cat.label}
                  </span>
                  {bp.divisionTags.map(d => (
                    <span key={d} style={{ fontSize: 10, color: T.textMuted, background: T.bgApp, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "2px 7px" }}>
                      {divLabel(d)}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <div style={{ fontSize: 13, color: T.textPrimary, lineHeight: 1.55, marginBottom: bp.sourceNote ? 5 : 0 }}>
                  {bp.description}
                </div>

                {/* Source note */}
                {bp.sourceNote && (
                  <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic", lineHeight: 1.4 }}>
                    ↳ {bp.sourceNote}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 5, flexShrink: 0, marginTop: 2 }}>
                <button onClick={() => setEditItem({ ...bp })}
                  style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 11 }}>
                  Edit
                </button>
                {deleteConfirmId === bp.id ? (
                  <>
                    <button onClick={() => handleDelete(bp.id)}
                      style={{ background: "#fc8181", border: "none", color: "#fff", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 11, fontWeight: 700 }}>
                      Delete
                    </button>
                    <button onClick={() => setDeleteConfirmId(null)}
                      style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 11 }}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button onClick={() => setDeleteConfirmId(bp.id)}
                    style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 11 }}>
                    ×
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Bottom padding */}
        <div style={{ height: 40 }} />
      </div>

      {/* ── Add / Edit modal ── */}
      {editItem && (
        <BPModal item={editItem} T={T} onSave={handleSave} onClose={() => setEditItem(null)} />
      )}
    </div>
  );
}

// ── Add / Edit modal ─────────────────────────────────────────────────────────

export function BPModal({
  item, T, onSave, onClose,
}: {
  item: Partial<BestPractice>;
  T: any;
  onSave: (bp: BestPractice) => void;
  onClose: () => void;
}) {
  const [desc, setDesc] = useState(item.description || "");
  const [cat, setCat] = useState<BestPracticeCategory>(item.category || "eo");
  const [divTags, setDivTags] = useState<string[]>(item.divisionTags || []);
  const [sourceNote, setSourceNote] = useState(item.sourceNote || "");
  const [error, setError] = useState("");

  const toggleDiv = (id: string) =>
    setDivTags(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSubmit = () => {
    if (!desc.trim()) { setError("Description is required."); return; }
    if (divTags.length === 0) { setError("Select at least one division."); return; }
    onSave({
      id: item.id!,
      description: desc.trim(),
      divisionTags: divTags,
      category: cat,
      sourceNote: sourceNote.trim() || undefined,
      addedAt: item.addedAt || new Date().toISOString().slice(0, 10),
    });
  };

  const isNew = !item.description;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "#000b", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: "10px 10px 0 0", width: "100%", maxWidth: 620, maxHeight: "88vh", overflowY: "auto", padding: "28px 28px 44px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary }}>
            {isNew ? "New Best Practice" : "Edit Entry"}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
        </div>

        {/* Description */}
        <label style={{ display: "block", fontSize: 10, color: T.textMuted, letterSpacing: ".09em", textTransform: "uppercase", marginBottom: 6 }}>Description</label>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3}
          placeholder="e.g. Envelope mock-up fabrication and testing per project specifications"
          style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: "'Inter',sans-serif", fontSize: 13, padding: "10px 12px", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 }}
        />

        {/* Category */}
        <label style={{ display: "block", fontSize: 10, color: T.textMuted, letterSpacing: ".09em", textTransform: "uppercase", marginBottom: 8, marginTop: 18 }}>Category</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {CATEGORIES.map(c => (
            <button key={c.id} onClick={() => setCat(c.id)}
              style={{ padding: "6px 14px", borderRadius: 999, border: `1px solid ${cat === c.id ? c.color : T.borderSecondary}`, background: cat === c.id ? c.bg : "transparent", color: cat === c.id ? c.color : T.textMuted, cursor: "pointer", fontFamily: "'Inter',sans-serif", fontSize: 11, fontWeight: cat === c.id ? 700 : 400, transition: "all .12s" }}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Division tags */}
        <label style={{ display: "block", fontSize: 10, color: T.textMuted, letterSpacing: ".09em", textTransform: "uppercase", marginBottom: 8, marginTop: 18 }}>
          Division Tags <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>(select all that apply)</span>
        </label>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {ALL_DIVS.map(d => (
            <button key={d.id} onClick={() => toggleDiv(d.id)}
              style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${divTags.includes(d.id) ? TEAL : T.borderSecondary}`, background: divTags.includes(d.id) ? TEAL + "18" : "transparent", color: divTags.includes(d.id) ? TEAL : T.textMuted, cursor: "pointer", fontFamily: "'Inter',sans-serif", fontSize: 10, fontWeight: divTags.includes(d.id) ? 700 : 400, transition: "all .12s" }}>
              {d.code}
            </button>
          ))}
        </div>

        {/* Source note */}
        <label style={{ display: "block", fontSize: 10, color: T.textMuted, letterSpacing: ".09em", textTransform: "uppercase", marginBottom: 6, marginTop: 18 }}>Note / Source <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
        <input value={sourceNote} onChange={e => setSourceNote(e.target.value)}
          placeholder="e.g. Became a CO on Project X during DD phase"
          style={{ width: "100%", background: T.bgApp, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: "'Inter',sans-serif", fontSize: 12, padding: "8px 12px", outline: "none", boxSizing: "border-box" }} />

        {error && <div style={{ color: "#fc8181", fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 22 }}>
          <button onClick={handleSubmit}
            style={{ flex: 1, background: TEAL, border: "none", color: "#0f1117", padding: "11px 0", borderRadius: 6, fontFamily: "'Inter',sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {isNew ? "Add Entry" : "Save Changes"}
          </button>
          <button onClick={onClose}
            style={{ padding: "11px 22px", background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, borderRadius: 6, cursor: "pointer", fontFamily: "'Inter',sans-serif", fontSize: 13 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
